import { useOnboardingAnswersOptional } from '@/services/db/onboarding-answers/queries';
import { hasAnsweredOnboarding } from './answered';
import { useIsAccountVerified } from './account-verification';
import { useEssentialsOnboardingGate } from './completion';
import type { OnboardingEntryInput } from './entry-redirect';

/** Exactly what the entry selector reads, plus the hook's own loading state. */
export type OnboardingSignals = OnboardingEntryInput & {
  /** While true every other field is provisional — hold, don't act on them. */
  isLoading: boolean;
};

/** Every input the onboarding routing decisions read, gathered once. */
export function useOnboardingSignals(): OnboardingSignals {
  const { isEligible, isComplete, isLoading: isGateLoading } = useEssentialsOnboardingGate();
  const isVerified = useIsAccountVerified();
  // Cohort-gated so ineligible accounts fire no request.
  const answersQuery = useOnboardingAnswersOptional({ queryConfig: { enabled: isEligible } });

  // A disabled query stays pending forever, hence the isEligible guard.
  const isLoading = isGateLoading || (isEligible && answersQuery.isPending);

  return {
    isEligible,
    isComplete,
    isVerified,
    hasAnswered: hasAnsweredOnboarding(answersQuery.data?.answers),
    hasUntrustedSignal: answersQuery.isError,
    isLoading,
  };
}
